import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StockModal from '@/components/StockModal';
import { fetchStockWithFallback } from '@/lib/stockFetch';
import { useAuth } from '@/contexts/AuthContext';

// Hosts the Stock Modal on a standalone SEO page (ex-dividend-date,
// is-a-dividend-stock). Opens IN PLACE — no navigation, no reload, nothing
// opened in the background — and closing leaves the reader exactly where
// they were. Same open/close contract as LearnPage's openLearnStock.
export function useSeoStockModal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [stock, setStock] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialTab, setInitialTab] = useState('dividend-info');

  const load = useCallback(async (ticker) => {
    setLoading(true);
    try {
      const data = await fetchStockWithFallback(ticker, { timeoutMs: 3500, onFresh: (fresh) => setStock(fresh) });
      setStock(data);
    } catch {
      setStock(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const openStock = useCallback((ticker, tab = 'dividend-info') => {
    if (!ticker) return;
    setInitialTab(tab);
    setOpen(true);
    load(ticker);
  }, [load]);

  const close = useCallback(() => {
    setOpen(false);
    setStock(null);
    try { sessionStorage.removeItem('dividrip:modal-state'); } catch { /* ignore */ }
  }, []);

  const modal = (
    <StockModal
      stock={stock}
      open={open}
      onClose={close}
      loading={loading}
      isDark={typeof document !== 'undefined' && document.documentElement.classList.contains('dark')}
      user={user}
      onLoginRequired={() => navigate('/?login=1')}
      initialTab={initialTab}
      onSelectStock={load}
    />
  );
  return { openStock, modal };
}
